export type FeeCalculationStatus = "exact" | "estimated" | "unavailable";

export interface PaymentComponent {
  code: string;
  name: string;
  amount?: number;
  isDiscount: boolean;
}

export interface PaymentBreakdown {
  components: PaymentComponent[];
  totalAmount: number;
  status: FeeCalculationStatus;
  source: "component_amounts" | "actual_amount" | "initial_amount" | "none";
  fallbackCode?: string;
  reason?: string;
}

interface PaymentAlias {
  code: string;
  name: string;
  aliases: string[];
  isDiscount?: boolean;
}

const PAYMENT_ALIASES: PaymentAlias[] = [
  { code: "card", name: "신용카드", aliases: ["card", "신용카드"] },
  {
    code: "cash",
    name: "무통장입금",
    aliases: ["cash", "무통장", "무통장입금"],
  },
  { code: "tcash", name: "계좌이체", aliases: ["tcash", "계좌이체"] },
  { code: "icash", name: "가상계좌", aliases: ["icash", "가상계좌"] },
  { code: "cell", name: "휴대폰", aliases: ["cell", "휴대폰", "휴대폰결제"] },
  {
    code: "point",
    name: "선불금",
    aliases: ["point", "prepaid", "선불금", "선불"],
  },
  {
    code: "mileage",
    name: "적립금",
    aliases: ["mileage", "millage", "적립금", "마일리지"],
  },
  {
    code: "deposit",
    name: "예치금",
    aliases: ["deposit", "credit", "예치금", "크레딧"],
  },
  { code: "deferpay", name: "후불", aliases: ["deferpay", "cod", "후불"] },
  { code: "cvs", name: "편의점", aliases: ["cvs", "편의점"] },
  {
    code: "giftcard",
    name: "제휴상품권",
    aliases: ["giftcard", "제휴상품권"],
  },
  {
    code: "pointcard",
    name: "제휴포인트",
    aliases: ["pointcard", "제휴포인트"],
  },
  { code: "etc", name: "기타", aliases: ["etc", "기타"] },
  {
    code: "coupon",
    name: "쿠폰",
    aliases: ["coupon", "쿠폰", "할인쿠폰"],
    isDiscount: true,
  },
  {
    code: "market_discount",
    name: "마켓할인",
    aliases: ["market_discount", "마켓할인"],
    isDiscount: true,
  },
  {
    code: "discount_code",
    name: "프로모션 할인",
    aliases: ["discount_code", "프로모션", "프로모션 할인"],
    isDiscount: true,
  },
];

const ALIAS_LOOKUP = new Map<string, PaymentAlias>();
PAYMENT_ALIASES.forEach((item) => {
  item.aliases.forEach((alias) => ALIAS_LOOKUP.set(alias.toLowerCase(), item));
});

function toNumber(value: any): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || !value.trim()) return undefined;
  const parsed = Number(value.replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : undefined;
}

function readAmount(value: any): number | undefined {
  const direct = toNumber(value);
  if (direct !== undefined) return Math.max(0, direct);
  if (!value || typeof value !== "object") return undefined;

  const candidates = [
    "payment_amount",
    "actual_payment_amount",
    "total_amount",
    "total_amount_due",
    "order_price_amount",
    "payed_amount",
    "amount",
  ];
  for (const key of candidates) {
    const amount = toNumber(value[key]);
    if (amount !== undefined) return Math.max(0, amount);
  }
  return undefined;
}

export function tokenizePaymentMethods(raw: any): string[] {
  const source = Array.isArray(raw)
    ? raw
        .map((item) =>
          typeof item === "object" && item !== null
            ? item.payment_method ||
              item.payment_method_name ||
              item.name ||
              item.code
            : item
        )
        .filter(Boolean)
        .join(",")
    : String(raw || "");

  return source
    .split(/[,/|+]|\s+\/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);
}

export function resolvePaymentAlias(raw: any): PaymentAlias | undefined {
  const token = String(raw || "")
    .trim()
    .toLowerCase();
  if (!token) return undefined;

  const exact = ALIAS_LOOKUP.get(token);
  if (exact) return exact;

  const matching = PAYMENT_ALIASES.filter((item) =>
    item.aliases.some(
      (alias) =>
        token.includes(alias.toLowerCase()) ||
        alias.toLowerCase().includes(token)
    )
  );
  return matching.sort((a, b) => b.name.length - a.name.length)[0];
}

function createGenericAlias(raw: any): PaymentAlias | undefined {
  const name = String(raw || "").trim();
  if (!name || name === "정보 없음") return undefined;
  return {
    code: name.toLowerCase().replace(/\s+/g, "_"),
    name,
    aliases: [],
  };
}

function resolveTokenAlias(raw: any): PaymentAlias | undefined {
  return resolvePaymentAlias(raw) || createGenericAlias(raw);
}

function readRawComponents(item: any): any[] | undefined {
  const candidates = [
    item?.payment_components,
    item?.payment_breakdown,
    item?.payment_details,
    item?.actual_order_amount?.payment_components,
    item?.actual_order_amount?.payment_breakdown,
  ];
  return candidates.find((value) => Array.isArray(value));
}

function readTotalAmount(item: any): {
  amount?: number;
  source: PaymentBreakdown["source"];
} {
  const actualCandidates = [
    item?.actual_order_amount,
    item?.actual_payment_amount,
    item?.actual_amount,
    item?.payment_amount,
  ];
  for (const candidate of actualCandidates) {
    const amount = readAmount(candidate);
    if (amount !== undefined) return { amount, source: "actual_amount" };
  }

  const initial = readAmount(item?.initial_order_amount);
  if (initial !== undefined)
    return { amount: initial, source: "initial_amount" };
  return { source: "none" };
}

function componentFromRaw(raw: any): PaymentComponent | undefined {
  const alias = resolveTokenAlias(
    raw?.payment_method ||
      raw?.payment_method_name ||
      raw?.name ||
      raw?.code ||
      raw
  );
  if (!alias) return undefined;
  const amount = readAmount(raw?.amount ?? raw?.payment_amount ?? raw?.value);
  return {
    code: alias.code,
    name: alias.name,
    amount,
    isDiscount: Boolean(alias.isDiscount),
  };
}

export function resolvePaymentBreakdown(item: any): PaymentBreakdown {
  const total = readTotalAmount(item);
  const rawComponents = readRawComponents(item);
  if (rawComponents && rawComponents.length > 0) {
    const components = rawComponents
      .map(componentFromRaw)
      .filter(Boolean) as PaymentComponent[];
    const componentAmount = components.every(
      (component) => component.amount !== undefined
    );
    if (components.length > 0 && componentAmount) {
      return {
        components,
        totalAmount:
          total.amount ??
          components.reduce((sum, item) => sum + (item.amount || 0), 0),
        status: "exact",
        source: "component_amounts",
      };
    }
  }

  const rawComponentAliases = (rawComponents || [])
    .map((component) =>
      resolveTokenAlias(
        component?.payment_method ||
          component?.payment_method_name ||
          component?.name ||
          component?.code
      )
    )
    .filter(Boolean) as PaymentAlias[];
  const rawSources = [item?.payment_method, item?.payment_method_name].filter(
    Boolean
  );
  const tokens = rawSources.flatMap((source) => tokenizePaymentMethods(source));
  const aliases = (
    rawComponentAliases.length > 0
      ? rawComponentAliases
      : tokens.map(resolveTokenAlias)
  ).filter(Boolean) as PaymentAlias[];
  const uniqueAliases = aliases.filter(
    (alias, index) =>
      aliases.findIndex((candidate) => candidate.code === alias.code) === index
  );
  const amount = total.amount;

  if (uniqueAliases.length === 0) {
    return {
      components: [],
      totalAmount: amount || 0,
      status: "unavailable",
      source: total.source,
      reason:
        amount === undefined
          ? "실제 결제금액과 결제수단을 확인할 수 없습니다."
          : "결제수단을 확인할 수 없습니다.",
    };
  }

  const components = uniqueAliases.map((alias) => ({
    code: alias.code,
    name: alias.name,
    isDiscount: Boolean(alias.isDiscount),
  }));
  if (amount === undefined) {
    return {
      components,
      totalAmount: 0,
      status: "unavailable",
      source: total.source,
      reason: "실제 결제금액을 확인할 수 없습니다.",
    };
  }

  const isComposite =
    uniqueAliases.length > 1 || rawComponentAliases.length > 1;
  const hasFinalAmount = total.source === "actual_amount";
  const primary =
    uniqueAliases.find((alias) => !alias.isDiscount) || uniqueAliases[0];
  return {
    components,
    totalAmount: amount,
    status: isComposite || !hasFinalAmount ? "estimated" : "exact",
    source: total.source,
    fallbackCode: primary.code,
    reason:
      isComposite || !hasFinalAmount
        ? isComposite
          ? "복합 결제수단별 실제 금액을 확인할 수 없어 대표 결제수단으로 추정했습니다."
          : "현재 결제금액이 없어 최초 결제금액 기준으로 계산했습니다."
        : undefined,
  };
}

export function formatPaymentBreakdown(breakdown: PaymentBreakdown): string {
  if (breakdown.components.length === 0) return "정보 없음";
  return breakdown.components
    .map((component) =>
      component.amount === undefined
        ? component.name
        : `${component.name} ${component.amount.toLocaleString()}원`
    )
    .join(" + ");
}
